<template>
  <FormCard
    v-for="fieldGroup in fieldGroups"
    :key="fieldGroup.key"
    :data-testid="`self-service-form-manual-group-${fieldGroup.key}`"
    :data-group-key="fieldGroup.key"
    :title="fieldGroup.name"
    :class="{ 'selected-card': openCard === fieldGroup.key }"
    :is-open="openCard === fieldGroup.key"
    :completed="completedCards.has(fieldGroup.key)"
    @toggle="handleFormCardToggle(fieldGroup.key)"
    @enter-pressed="handleSaveAndContinue(fieldGroup.key)">
    <template #form-content>
      <div
        v-for="field in fieldGroup.properties"
        :key="field.key"
        class="form-section"
        :data-testid="`self-service-field-${field.key}`"
        :data-field-key="field.key">
        <LeadSelect
          v-if="field.type === 'select'"
          :label="field.label"
          :options="field.options?.select_options ?? []"
          :required="field.required"
          :errors="flatErrors(errors[field.key])"
          :model-value="obj[field.key] ?? null"
          @update:modelValue="obj[field.key] = $event" />

        <LeadDatePicker
          v-else-if="field.type === 'date'"
          :label="field.label"
          :required="field.required"
          :errors="flatErrors(errors[field.key])"
          :min-date="field.options?.min_date ?? null"
          :max-date="field.options?.max_date ?? null"
          :model-value="(obj[field.key] as string) ?? null"
          @update:modelValue="obj[field.key] = $event" />

        <LeadCheckbox
          v-else-if="field.type === 'switch'"
          :label="field.label"
          :is-required="field.required"
          :errors="flatErrors(errors[field.key])"
          :model-value="obj[field.key] === true"
          @update:modelValue="obj[field.key] = $event" />

        <LeadManualImage
          v-else-if="field.type === 'image'"
          v-model="obj[field.key]"
          :label="field.label"
          :hint="field.description"
          :required="field.required"
          :errors="flatErrors(errors[field.key])"
          :cut-width="field.options?.cut_width ?? null"
          :cut-height="field.options?.cut_height ?? null"
          :max-file-size-kb="field.options?.max_file_size_kb ?? null" />

        <LeadManualLocation
          v-else-if="field.type === 'location'"
          v-model="obj[field.key]"
          :label="field.label"
          :hint="field.description"
          :required="field.required"
          :errors="flatErrors(errors[field.key])"
          :max-locations="field.options?.max_locations ?? null" />

        <LeadInput
          v-else-if="field.type === 'number'"
          type="number"
          :label="field.label"
          :hint="field.description"
          :is-required="field.required"
          :errors="flatErrors(errors[field.key])"
          :model-value="obj[field.key] ?? null"
          @update:modelValue="obj[field.key] = toNumber($event)" />

        <!-- text, and anything a newer PJAS adds that this build does not recognise yet: a text box
             still lets the customer answer, where an unhandled type would render nothing at all -->
        <LeadInput
          v-else
          :type="field.options?.multiline ? 'textarea' : 'text'"
          :rows="field.options?.multiline ? 4 : undefined"
          :label="field.label"
          :hint="field.description"
          :is-required="field.required"
          :maxlength="field.options?.max_length ?? undefined"
          :errors="flatErrors(errors[field.key])"
          :model-value="obj[field.key] ?? ''"
          @update:modelValue="obj[field.key] = $event" />
      </div>

      <div
        class="form-actions float-right"
        :data-testid="`self-service-save-action-${fieldGroup.key}`"
        :data-i18n-key="fieldGroup.key === lastGroupKey ? 'btn.save' : 'btn.save_and_continue'">
        <LeadButton variant="secondary" :loading="loadingSave" @click="handleSaveAndContinue(fieldGroup.key)">
          {{ fieldGroup.key === lastGroupKey ? $t('btn.save') : $t('btn.save_and_continue') }}
          <template v-if="fieldGroup.key !== lastGroupKey" #icon>
            <IconArrowBigRightFilled size="16" stroke-width="1.5" />
          </template>
        </LeadButton>
      </div>
    </template>
  </FormCard>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { IconArrowBigRightFilled } from '@tabler/icons-vue';
import FormCard from '@/components/orders/form_cards/FormCard.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import LeadCheckbox from '@/components/form/LeadCheckbox.vue';
import LeadDatePicker from '@/components/form/LeadDatePicker.vue';
import LeadInput from '@/components/form/LeadInput.vue';
import LeadManualImage from '@/components/form/LeadManualImage.vue';
import LeadManualLocation from '@/components/form/LeadManualLocation.vue';
import LeadSelect from '@/components/form/LeadSelect.vue';
import { updateCampaignObject } from '@/api/campaign';
import { flatErrors } from '@/helpers/helpers';
import { useProviderStore } from '@/stores/providers';
import type { FieldGroup } from '@/types/types';

/**
 * The self-service form of a manual booking.
 *
 * Unlike its siblings it carries no per-field special cases, because a Manual Provider Type's fields
 * are instance configuration: the partner, the tabs and every field are chosen by an administrator
 * in PJAS, so there is nothing to hardcode. The groups, the widget for each field, its bounds, its
 * hint and whether it is required all arrive from `GET /v1/account/campaigns/{id}/fields`, and this
 * component only decides which input renders each `type`.
 *
 * An unrecognised `type` deliberately falls through to a text box rather than rendering nothing: a
 * newer PJAS may serve a field kind this build has never heard of, and a customer being unable to
 * answer is worse than answering in the wrong widget.
 */
const props = defineProps<{
  providerId: string | null | undefined;
  fieldGroups: FieldGroup[];
  object: any;
}>();

const emit = defineEmits<{
  (e: 'update:object', obj: any): void;
}>();

const providerStore = useProviderStore();
const { completedDecks, obj } = storeToRefs(providerStore);

const openCard = ref<string | null>(props.fieldGroups[0]?.key ?? null);
const loadingSave = ref(false);
const errors = ref<Record<string, any>>({});

const completedCards = computed(() => {
  if (!props.providerId) return new Set<string>();

  return completedDecks.value[props.providerId] || new Set<string>();
});

const lastGroupKey = computed(() => props.fieldGroups[props.fieldGroups.length - 1]?.key);

/** An emptied number input is null rather than 0 — "not filled in" is not "zero". */
function toNumber(value: unknown): number | null {
  const text = String(value ?? '').trim();

  return text === '' ? null : Number(text);
}

function handleFormCardToggle(key: string) {
  openCard.value = openCard.value === key ? null : key;
}

async function handleSaveAndContinue(currentCard: string) {
  loadingSave.value = true;

  // only this card's fields, so saving one tab cannot resubmit a neighbour the customer has not
  // reached yet
  const index = props.fieldGroups.findIndex((candidate) => candidate.key === currentCard);
  const updateData: Record<string, unknown> = {};

  (props.fieldGroups[index]?.properties ?? []).forEach((field) => {
    if (obj.value[field.key] !== undefined) {
      updateData[field.key] = obj.value[field.key];
    }
  });

  try {
    const res = await updateCampaignObject(props.providerId ?? '', updateData);

    emit('update:object', res.data);
    errors.value = {};

    providerStore.addCompletedCard(currentCard, props.providerId ?? '');

    openCard.value = index >= 0 && index < props.fieldGroups.length - 1 ? props.fieldGroups[index + 1].key : null;
  } catch (error: any) {
    if (error.responseJson) {
      errors.value = error.responseJson.errors?.provider_values || {};
    } else {
      console.error('Unexpected error:', error);
    }
  } finally {
    loadingSave.value = false;
  }
}
</script>

<style scoped lang="scss">
.float-right {
  float: right;
}

.form-section {
  margin-bottom: 30px;
}
</style>
